import { Alert, Backdrop, Box, Button, CircularProgress, Paper, Stack, Typography, } from "@mui/material";
import { useState } from "react";
import MenuFrame from "../frames/menu_frame";
import { getOrgDetails, getScopeList } from "./admin_helper";
import { getAllUsers } from "../users/user_halper";
import OrgProfile from "./org_profile";
import BillingSettings from "./billing_settings";
import BankChange from "./bank_change";
import AuthAdmin from "./permission";
import { getUserAccess } from "../frames/common";
import FileUpload from "./file_upload";
import "./file_upload.css";

function Admin() {
    const [alertMessage, setAlertMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userList, setUserList] = useState(null);
    const [alternate, setAlternate] = useState(0);
    const [orgDetails, setOrgDetails] = useState(null);
    const [permissionList, setPermissionList] = useState(null);

    const stateChange = (loading) => {
        setIsLoading(loading);
    };

    const errorChange = (message) => {
        setAlertMessage(message || null);
    };

    const initData = async (auth) => {
        const hasAdminAccess = await getUserAccess("AUTH_ADMIN");
        if (!auth && !hasAdminAccess) {
            return;
        }
        if (!auth && hasAdminAccess) {
            setAlternate(1);
        } else if (auth && hasAdminAccess) {
            setAlternate(2);
        }
        if (hasAdminAccess) {
            const [users, scopes] = await Promise.all([getAllUsers(), getScopeList()]);
            setUserList(users);
            setPermissionList(scopes);
        }
        const organization = await getOrgDetails();
        setOrgDetails(organization);
    };

    const fileUpload = async (event) => {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const file = formData.get("logo");
        console.log(file);
        // Add upload API here.
    };

    const LoadingOverlay = () => (
        <Backdrop open={isLoading} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 2, backdropFilter: "blur(3px)", backgroundColor: "rgba(0, 0, 0, 0.35)" })}>
            <Stack sx={{ alignItems: "center" }} spacing={2}>
                <CircularProgress color="inherit" size={42} />
                <Typography variant="body2">Please wait...</Typography>
            </Stack>
        </Backdrop>
    );

    const AlertMessage = () => {
        if (!alertMessage) return null;

        return (
            <Alert severity={alertMessage.severity} variant="filled" onClose={() => setAlertMessage(null)} sx={{ mb: 3, borderRadius: 2, boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}>
                {alertMessage.message}
            </Alert>
        );
    };

    const LogoUpload = () => (
        <Paper elevation={0} sx={{ mt: 3, mb: 5, p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider", borderRadius: 3, background: "linear-gradient(145deg, rgba(255,255,255,1), rgba(248,249,252,1))" }}>
            <Stack spacing={3}>
                {/* Header */}
                <Box>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Organization Logo</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Upload a logo that represents your organization.</Typography>
                </Box>

                <Box sx={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed", borderColor: "divider", borderRadius: 2.5, backgroundColor: "background.default", p: 2 }}>
                    {orgDetails?.logo ?
                        <Box component="img" src={orgDetails.logo} alt="Organization logo" sx={{ maxWidth: 180, maxHeight: 120, width: "auto", height: "auto", objectFit: "contain", borderRadius: 1 }} />
                        :
                        <Stack spacing={1} sx={{ color: "text.secondary", alignItems: "center" }}>
                            <Box sx={{ width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "action.hover", fontSize: 24 }}>🖼️</Box>
                            <Typography variant="body2">No logo uploaded</Typography>
                        </Stack>
                    }
                </Box>
                <Box component="form" onSubmit={fileUpload}>
                    <Stack spacing={2}>
                        <FileUpload name="logo" required />
                        <Button variant="contained" color="secondary" fullWidth type="submit" sx={{ minHeight: 46, borderRadius: 2, textTransform: "none", fontWeight: 600, boxShadow: "none", "&:hover": { boxShadow: "0 6px 18px rgba(0,0,0,0.12)", } }}>
                            Save Logo
                        </Button>
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    );

    if (alternate === 1) {
        return (
            <Box sx={{ minHeight: "100vh", p: { xs: 2, sm: 3 }, backgroundColor: "#f7f8fa" }}>
                <AlertMessage />
                <LoadingOverlay />
                <AuthAdmin user={userList} permission={permissionList} formLoading={stateChange} formError={errorChange} />
            </Box>
        );
    }

    return (
        <MenuFrame permission="ORG_ADMIN" onInit={initData}>
            <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto", pb: 3, }}>
                <AlertMessage />
                <LoadingOverlay />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" fontWeight={700} color="text.primary">Organization Settings</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage your organization, billing, banking, and administration settings.
                    </Typography>
                </Box>

                {alternate === 2 ? (
                    <Box sx={{ mb: 3 }}>
                        <AuthAdmin user={userList} permission={permissionList} formLoading={stateChange} formError={errorChange} />
                    </Box>
                ) : null}

                <Stack spacing={2.5}>
                    <BankChange data={orgDetails} onStateChange={stateChange} />
                    <BillingSettings data={orgDetails} onStateChange={stateChange} />
                    <OrgProfile data={orgDetails} onStateChange={stateChange} />
                </Stack>

                <LogoUpload />
            </Box>
        </MenuFrame>
    );
}

export default Admin;
